import React, { useState } from 'react';
import AvailLanguageChip from './AvailLanguageChip';
import styled from '@emotion/styled';
import { Box, ClickAwayListener, Popper } from '@mui/material';
import { FocusAvail } from '@/redux/slices/slice_types';
import { Theme } from '@emotion/react';
import { dubRights, subRights } from './constants';
import PolarisIcon from '../_common/PolarisIcon';

interface AvailLanguageChipListProps {
  languages: string[];
  type: 'sub' | 'dub';
  elements?: FocusAvail['elements'];
  countToDisplay?: number;
  columns?: number;
  displayOnly?: boolean;
  availedAVLanguage?: string;
  noInventory?: boolean;
}

const getDisplayLanguages = (languages: string[], type: 'sub' | 'dub', countToDisplay: number, displayOnly: boolean) => {
  const allRights = type === 'sub' ? subRights : dubRights;
  const combined = displayOnly ? [...languages] : Array.from(new Set([...languages, ...allRights]));

  const sorted = combined.sort((a, b) => a.localeCompare(b));
  const overflowCount = sorted.length - countToDisplay;

  return {
    overflowCount,
    displayLanguages: overflowCount + 1 >= sorted.length ? sorted : sorted.slice(0, countToDisplay),
    sortedAll: sorted
  };
};

const AvailLanguageChipList: React.FC<AvailLanguageChipListProps> = ({
  languages,
  elements,
  type,
  countToDisplay = 9,
  columns = 5,
  displayOnly = false,
  availedAVLanguage,
  noInventory
}) => {
  const { overflowCount, displayLanguages, sortedAll } = getDisplayLanguages(languages, type, countToDisplay, displayOnly);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const overflowOpen = Boolean(anchorEl);
  const [curLanguages, setCurLanguages] = useState<string[]>(languages || []);

  const handleOverflowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setAnchorEl(anchorEl ? null : e.currentTarget);
  };

  const handleSelectClick = (lang: string, e: React.MouseEvent<HTMLDivElement>) => {
    if (!displayOnly) {
      e.preventDefault();
      e.stopPropagation();
      if (curLanguages.includes(lang)) {
        setCurLanguages(curLanguages.filter((l) => l !== lang));
      } else {
        setCurLanguages([...curLanguages, lang]);
      }
    }
  };

  return (
    <ChipContainer columns={columns}>
      {displayLanguages.map((lang) => {
        const isChipEnabled = displayOnly || curLanguages.includes(lang);
        return (
          <AvailLanguageChip isPointer={!displayOnly} onClick={(e) => handleSelectClick(lang, e)} enabled={isChipEnabled} key={lang}>
            <InnerContainer noInventory={noInventory}>
              {!noInventory && (
                <CircleContainer>
                  <Circle color={getColor(lang, elements, isChipEnabled, availedAVLanguage) || 'error'} />
                </CircleContainer>
              )}
              <LangText noInventory={noInventory}>{lang}</LangText>
            </InnerContainer>
          </AvailLanguageChip>
        );
      })}

      {overflowCount > 1 && (
        <>
          <AvailLanguageChip onClick={handleOverflowClick} enabled={overflowOpen} isPointer>
            +{overflowCount} <PolarisIcon icon="down_caret" />
          </AvailLanguageChip>
          <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
            <Popper
              open={overflowOpen}
              anchorEl={anchorEl}
              placement="bottom-end"
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [8, 0]
                  }
                }
              ]}
            >
              <OverflowContainer columns={columns}>
                {sortedAll.slice(countToDisplay).map((lang) => {
                  const isChipEnabled = displayOnly || curLanguages.includes(lang);
                  return (
                    <AvailLanguageChip isPointer={!displayOnly} onClick={(e) => handleSelectClick(lang, e)} enabled={isChipEnabled} key={lang}>
                      <InnerContainer noInventory={noInventory}>
                        {!noInventory && (
                          <CircleContainer>
                            <Circle color={getColor(lang, elements, isChipEnabled, availedAVLanguage) || 'error'} />
                          </CircleContainer>
                        )}
                        <LangText noInventory={noInventory}>{lang}</LangText>
                      </InnerContainer>
                    </AvailLanguageChip>
                  );
                })}
              </OverflowContainer>
            </Popper>
          </ClickAwayListener>
        </>
      )}
    </ChipContainer>
  );
};

export default AvailLanguageChipList;

const InnerContainer = styled.div<{ noInventory?: boolean }>`
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: ${({ noInventory }) => (noInventory ? 'center' : 'flex-start')};
`;

const CircleContainer = styled.div`
  margin: 5.5px 10px 0 5px;
`;

const Circle = styled.div<{ color: keyof Theme['palette'] }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ theme, color }) => theme.palette[color].main};
`;

const ChipContainer = styled.div<{ columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
  gap: 3px;
`;

const OverflowContainer = styled(Box)<{ columns: number }>`
  background-color: ${({ theme }) => theme.palette.background.paper};
  padding: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(0.5)};
  display: grid;
  grid-template-columns: repeat(${({ columns }) => columns}, 1fr);
  gap: 3px;
  border: 1px solid ${({ theme }) => theme.palette.divider};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
`;

const LangText = styled.div<{ noInventory?: boolean }>`
  white-space: nowrap;
  width: ${({ noInventory }) => (noInventory ? 'auto' : '100%')};
`;

const getColor = (lang: string, elements?: FocusAvail['elements'], isEnabled?: boolean, availedAVLanguage?: string) => {
  if (!elements) return undefined;

  let element = elements.find((comp) => comp.element.language === lang && comp.element.element_type !== 'video');
  if (!isEnabled) return 'default';
  if (availedAVLanguage && lang === availedAVLanguage) {
    element = elements.find((comp) => comp.element.language === lang && comp.element.element_type === 'video');
  }
  if (!element) return undefined;

  switch (element.inventory_status) {
    // case 'available':
    //   return 'success';
    // case 'missing':
    // case 'pending':
    // case 'in_progress':
    // case 'unavailable':
    //   return 'warning';
    // case 'error':
    //   return 'error';
    // default:
    //   return 'info';
    case 'available':
      return 'success';
    case 'missing':
      return 'warning';
    case 'error':
      return 'error';
    default:
      return 'info';
  }
};
